import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, EmptyState } from "@/components/Common";
import { webauthnSupported, webauthnCreate } from "@/lib/webauthnHelpers";
import { toast } from "sonner";
import { Fingerprint, Save, Trash2 } from "lucide-react";

/**
 * Enrollment tab (managers): enroll a staff member's fingerprint via the
 * browser (WebAuthn) and/or map the user ID assigned to them on a door
 * fingerprint terminal (Device ID).
 */
export default function AttendanceEnrollment({ isManager, refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [deviceIds, setDeviceIds] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/enrollments");
      setItems(data);
      setDeviceIds(Object.fromEntries(data.map((s) => [s.staff_id, s.device_user_id || ""])));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const enroll = async (s) => {
    if (!webauthnSupported()) {
      toast.error("This browser/device does not support fingerprint enrollment (WebAuthn needs HTTPS + a fingerprint-capable device)");
      return;
    }
    setBusyId(s.staff_id);
    try {
      const { data: options } = await api.post(`/attendance/webauthn/enroll-options/${s.staff_id}`);
      const credential = await webauthnCreate(options);
      await api.post(`/attendance/webauthn/enroll-verify/${s.staff_id}`, credential);
      toast.success(`Fingerprint enrolled for ${s.name}`);
      load(); onChanged?.();
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.error("Enrollment cancelled or timed out");
      else toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const saveMapping = async (s) => {
    try {
      await api.post("/attendance/staff-mapping", {
        staff_id: s.staff_id, device_user_id: deviceIds[s.staff_id] || null,
      });
      toast.success(`Device ID saved for ${s.name}`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCredential = async (s, cid) => {
    if (!window.confirm(`Remove this enrolled fingerprint for ${s.name}?`)) return;
    try {
      await api.delete(`/attendance/credentials/${cid}`);
      toast.success("Fingerprint removed");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((s) =>
    !q.trim() || `${s.name} ${s.staff_number}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-9 w-56" data-testid="enroll-search" />
        <div className="text-xs text-gray-500 ml-auto">
          Browser fingerprint = enroll on the actual kiosk device · Door terminal = set the Device ID
          the scanner assigned to the staff member
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading staff…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No staff found"
          hint="Add staff under Administration → Staff Bio Data first, then enroll their fingerprints here." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="enroll-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2.5">Staff</th>
                <th className="px-3 py-2.5">Branch</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Browser fingerprints</th>
                <th className="px-3 py-2.5">Door terminal Device ID</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.staff_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                  data-testid={`enroll-row-${s.staff_number}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">{s.name}</div>
                    <div className="text-[11px] text-gray-400">{s.staff_number}</div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{s.branch || "—"}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={s.enrolled ? "emerald" : "slate"}>
                      {s.enrolled ? "enrolled" : "not enrolled"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {s.credentials.map((c) => (
                        <span key={c.id} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full px-2 py-0.5">
                          <Fingerprint size={11} /> {String(c.created_at || "").slice(0, 10)}
                          {isManager && (
                            <button onClick={() => removeCredential(s, c.id)} title="Remove"
                              className="text-red-400 hover:text-red-600" data-testid={`enroll-remove-${c.id}`}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </span>
                      ))}
                      {isManager && (
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          disabled={busyId === s.staff_id}
                          onClick={() => enroll(s)} data-testid={`enroll-button-${s.staff_number}`}>
                          <Fingerprint size={12} className="mr-1" />
                          {busyId === s.staff_id ? "Waiting…" : "Enroll fingerprint"}
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Input value={deviceIds[s.staff_id] ?? ""} placeholder="e.g. 1042"
                        disabled={!isManager}
                        onChange={(e) => setDeviceIds((d) => ({ ...d, [s.staff_id]: e.target.value }))}
                        className="h-8 w-28 text-xs font-mono" data-testid={`enroll-devid-${s.staff_number}`} />
                      {isManager && (
                        <button onClick={() => saveMapping(s)} title="Save device ID"
                          className="p-1.5 rounded text-emerald-700 hover:bg-emerald-50"
                          data-testid={`enroll-devid-save-${s.staff_number}`}>
                          <Save size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
